// /reports/:id — tenant workspace: a single report (mockup 24, view 2). Scope
// header + active-scope chips + KPIs + an optional daily trend + ranked
// breakdowns. The period comes from the ?period query (set on the index);
// "Exportar PDF" prints the page (browser → save as PDF). Data via
// getReportDetail().

import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import {
  type Breakdown,
  getReportDetail,
  type ReportKpi,
  type ReportPeriod,
  type ReportTrend,
} from './reports-data.js'
import { Button, EmptyStateCard, PageHeader, Spinner } from '../../design-system/index.js'

const PERIODS: ReportPeriod[] = ['7d', '30d', '90d']

const DELTA_TONE = { up: 'text-ok', warn: 'text-warn', neutral: 'text-text-2' } as const

function KpiCard({ kpi }: { kpi: ReportKpi }): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-surface px-[18px] py-4">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
        {kpi.label}
      </div>
      <div className="mt-2 text-[24px] font-extrabold leading-[1.05] tracking-[-0.025em] tabular-nums text-text">
        {kpi.value}
        {kpi.unit && (
          <span className="ml-0.5 text-[14px] font-semibold text-text-2">{kpi.unit}</span>
        )}
      </div>
      {kpi.delta && (
        <div className={`mt-1.5 text-[11px] font-semibold ${DELTA_TONE[kpi.delta.tone]}`}>
          {kpi.delta.text}
        </div>
      )}
    </div>
  )
}

function points(values: number[], yMax: number, w = 600, h = 150): string {
  const n = values.length
  if (n === 0) return ''
  const safeYMax = yMax > 0 ? yMax : 1
  return values
    .map((v, i) => {
      // With a single point there is no span to divide across: pin it to the left
      // edge instead of dividing by zero.
      const x = n === 1 ? 0 : (i / (n - 1)) * w
      const y = h - Math.min(1, v / safeYMax) * h
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

const PERIOD_LABELS: Record<ReportPeriod, { current: string; previous: string }> = {
  '7d': { current: 'Últimos 7 días', previous: '7 días previos' },
  '30d': { current: 'Últimos 30 días', previous: '30 días previos' },
  '90d': { current: 'Últimos 90 días', previous: '90 días previos' },
}

function TrendChart({ trend, period }: { trend: ReportTrend; period: ReportPeriod }): JSX.Element {
  const w = 600
  const h = 150
  const labels = PERIOD_LABELS[period]
  const area = `M${points(trend.thisPeriod, trend.yMax, w, h).replace(/ /g, ' L')} L${w},${h} L0,${h} Z`
  return (
    <section className="mt-4 rounded-xl border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[13px] font-bold text-text">{trend.title}</h2>
        <span className="text-[11px] text-text-3">{trend.sub}</span>
        <span className="ml-auto flex items-center gap-3 text-[10.5px] text-text-2">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-brand-blue" aria-hidden />
            {labels.current}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-text-3" aria-hidden />
            {labels.previous}
          </span>
        </span>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="mt-3 h-[150px] w-full"
        role="img"
        aria-label={`${trend.title}: tendencia diaria, ${labels.current.toLowerCase()} contra el periodo anterior`}
      >
        <defs>
          <linearGradient id="rd-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand-blue)" stopOpacity="0.2" />
            <stop offset="100%" stopColor="var(--brand-blue)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1="0" y1={f * h} x2={w} y2={f * h} stroke="var(--line)" strokeWidth="1" />
        ))}
        <path d={area} fill="url(#rd-area)" />
        <polyline
          points={points(trend.lastPeriod, trend.yMax, w, h)}
          fill="none"
          stroke="var(--text-3)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
          opacity="0.6"
        />
        <polyline
          points={points(trend.thisPeriod, trend.yMax, w, h)}
          fill="none"
          stroke="var(--brand-blue)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="mt-1.5 flex justify-between text-[10px] text-text-3">
        {trend.xLabels.map((x) => (
          <span key={x}>{x}</span>
        ))}
      </div>
    </section>
  )
}

function BreakdownCard({ breakdown }: { breakdown: Breakdown }): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-3">
        <h2 className="text-[12.5px] font-bold text-text">{breakdown.title}</h2>
        {breakdown.meta && <span className="text-[11px] text-text-3">{breakdown.meta}</span>}
      </div>
      <ul className="flex flex-col gap-3 px-4 py-4">
        {breakdown.items.map((it) => (
          <li key={it.label} className="flex items-center gap-3">
            <span className="flex min-w-0 flex-1 items-center gap-2 text-[12px] text-text">
              <span aria-hidden className={`size-2 flex-none rounded-full ${it.color}`} />
              <span className="truncate">{it.label}</span>
            </span>
            <span className="hidden h-[6px] w-[110px] flex-none overflow-hidden rounded-pill bg-field sm:block">
              <span
                className={`block h-full rounded-pill ${it.color}`}
                style={{ width: `${it.pct}%` }}
              />
            </span>
            <span className="flex-none text-right text-[11.5px] tabular-nums text-text-2">
              <b className="font-mono font-bold text-text">{it.value}</b> {it.pct}%
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function ReportDetailPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const raw = search.get('period')
  const period: ReportPeriod = PERIODS.includes(raw as ReportPeriod) ? (raw as ReportPeriod) : '30d'
  const { data: report, isLoading } = useQuery({
    queryKey: ['report', id, period],
    queryFn: () => getReportDetail(id, period),
  })

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando reporte…" />
      </div>
    )
  }

  if (!report) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PageHeader title="Reporte no encontrado" />
        <div className="mt-6">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  d="M4 19V9m5 10V5m5 14v-7m5 7V8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            title="No encontramos este reporte"
            body="Puede que el enlace sea incorrecto."
            actions={<Button onClick={() => void navigate('/reportes')}>Volver a reportes</Button>}
          />
        </div>
      </div>
    )
  }

  const { scope } = report

  return (
    <div data-testid="report-detail" className="mx-auto w-full max-w-4xl">
      <PageHeader
        breadcrumb={[{ label: 'Reportes', href: '/reportes' }]}
        title={report.title}
        subtitle={`${scope.bot} · ${scope.period} · ${scope.channel}`}
        actions={
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            Exportar PDF
          </Button>
        }
      />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-text-3">
          Alcance
        </span>
        {report.filters.map((f) => (
          <span
            key={f}
            className="flex items-center gap-1.5 rounded-md border border-line bg-field px-2.5 py-1.5 text-[11.5px] font-medium text-text-2"
          >
            {f}
          </span>
        ))}
      </div>

      {report.kpis.length === 0 && report.breakdowns.length === 0 && !report.trend ? (
        <div className="mt-4">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  d="M4 19V9m5 10V5m5 14v-7m5 7V8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            title="Aún no hay datos para este periodo"
            body="Cuando haya actividad en el rango seleccionado, las métricas de este reporte aparecerán aquí."
          />
        </div>
      ) : (
        <>
          {report.kpis.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {report.kpis.map((k) => (
                <KpiCard key={k.label} kpi={k} />
              ))}
            </div>
          )}

          {report.trend && <TrendChart trend={report.trend} period={period} />}

          <div className="mt-4 grid grid-cols-1 gap-4">
            {report.breakdowns.map((b) => (
              <BreakdownCard key={b.title} breakdown={b} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
